import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface RootErrorBoundaryProps {
  children: ReactNode;
}

interface RootErrorBoundaryState {
  hasError: boolean;
}

export class RootErrorBoundary extends Component<
  RootErrorBoundaryProps,
  RootErrorBoundaryState
> {
  state: RootErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RootErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Keep diagnostics in console for local debugging and bug triage.
    console.error("Unhandled application error", error, errorInfo);
  }

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <main className="grid min-h-screen place-items-center p-4" role="alert" aria-live="assertive">
        <section className="w-[min(28rem,100%)] rounded-2xl border border-border bg-panel p-6 shadow-[0_20px_40px_-20px_#000,0_0_0_1px_color-mix(in_oklab,var(--color-brand-500),transparent_90%)] space-y-4">
          <h1 className="text-2xl font-semibold text-text">Something went wrong</h1>
          <p className="text-sm text-muted">
            An unexpected error interrupted the app. Reload to recover.
          </p>
          <button type="button" className="inline-flex w-full items-center justify-center rounded-lg border border-brand-500/70 bg-brand-500 px-3 py-2 text-sm font-semibold text-black hover:bg-brand-400" onClick={() => window.location.reload()}>
            Reload app
          </button>
        </section>
      </main>
    );
  }
}
